import React from 'react'
import {
  RadioGroup,
  RadioGroupItem,
  Input,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  IconButton
} from '@shopflo/ui'
import { X } from '@phosphor-icons/react'
import {
  AvailablePaymentMode,
  AdditionalChargeType,
  PaymentMode,
  PaymentModeScope
} from 'src/lib/types/paymentSettings'
import { useLocale } from 'src/lib/hooks/useLocal'
import { usePaymentChargeForm } from './hooks/usePaymentChargeForm'

interface PaymentChargeFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  availableModes: AvailablePaymentMode[]
  selectedMode: string | null
  scope: PaymentModeScope
  onModeChange: (mode: string) => void
  isEditMode?: boolean
  getModeData?: (mode: string) => PaymentMode
}

const PaymentChargeForm: React.FC<PaymentChargeFormProps> = ({
  open,
  onOpenChange,
  availableModes,
  selectedMode,
  scope,
  onModeChange,
  isEditMode = false
}) => {
  const { t } = useLocale()

  const {
    modesPayload,
    error,
    isSaving,
    additionalChargeType,
    additionalChargeValue,
    handleChargeTypeChange,
    handleChargeValueChange,
    handleSave,
    handleCancel
  } = usePaymentChargeForm({ open, selectedMode, scope, onOpenChange })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg bg-white !rounded-2xl p-0'>
        <DialogHeader>
          <div className='px-5 pt-4 flex justify-between items-center'>
            <DialogTitle className='!text-text-lg-medium text-zinc-700'>
              {isEditMode
                ? t('payment_additional_charges.edit_additional_charges')
                : t('payment_additional_charges.add_additional_charges_title')}
            </DialogTitle>
            <IconButton IconComponent={X} size='sm' variant='ghost' onClick={() => onOpenChange(false)} />
          </div>
        </DialogHeader>
        <div className='flex flex-col py-2 px-5'>
          <div className='flex flex-col gap-2'>
            <label className='text-text-sm-medium text-zinc-500 font-medium'>
              {t('payment_additional_charges.select_payment_method')}
            </label>
            <Select value={selectedMode || undefined} onValueChange={onModeChange}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder={t('payment_additional_charges.select_payment_method')} />
              </SelectTrigger>
              <SelectContent>
                {availableModes.length === 0 ? (
                  <div className='p-3 text-sm text-gray-dark text-center'>
                    {t('payment_additional_charges.no_payment_methods_available')}
                  </div>
                ) : (
                  availableModes.map(mode => {
                    const modeInState = modesPayload.find(m => m.mode === mode.mode)
                    return (
                      <SelectItem key={mode.mode} value={mode.mode}>
                        {modeInState?.metadata?.title || modeInState?.title || mode.mode}
                      </SelectItem>
                    )
                  })
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedMode && (
            <div className='mt-6'>
              <div className='flex flex-col gap-3'>
                <div className='flex flex-col gap-4'>
                  <div className='flex flex-col gap-2'>
                    <label className='text-text-sm-medium text-zinc-500'>
                      {t('payment_additional_charges.charge_type')}
                    </label>
                    <RadioGroup
                      value={additionalChargeType || ''}
                      onValueChange={handleChargeTypeChange}
                      className='flex gap-4'
                    >
                      <div className='flex items-center gap-2'>
                        <RadioGroupItem
                          value={AdditionalChargeType.PERCENTAGE}
                          id={`percentage-${selectedMode}`}
                          className='ui-accent-[#01cca7] checked:ui-accent-[#01cca7]'
                        />
                        <label htmlFor={`percentage-${selectedMode}`} className='text-sm text-carbon cursor-pointer'>
                          {t('payment_additional_charges.percentage')}
                        </label>
                      </div>
                      <div className='flex items-center gap-2'>
                        <RadioGroupItem
                          value={AdditionalChargeType.FLAT}
                          id={`flat-${selectedMode}`}
                          className='ui-accent-[#01cca7] checked:ui-accent-[#01cca7]'
                        />
                        <label htmlFor={`flat-${selectedMode}`} className='text-sm text-carbon cursor-pointer'>
                          {t('payment_additional_charges.flat')}
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {additionalChargeType && (
                    <div className='flex flex-col gap-2'>
                      <div className='flex flex-col gap-1.5'>
                        <Input
                          id={`charge-value-${selectedMode}`}
                          type='number'
                          inputMode='decimal'
                          value={additionalChargeValue?.toString() || '0'}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleChargeValueChange(e.target.value)
                          }}
                          dimension='md'
                          error={Boolean(error)}
                          min={additionalChargeType === AdditionalChargeType.PERCENTAGE ? 0 : 0}
                          max={additionalChargeType === AdditionalChargeType.PERCENTAGE ? 100 : undefined}
                          step={additionalChargeType === AdditionalChargeType.PERCENTAGE ? 0.01 : 1}
                        />
                        {error && <p className='text-sm text-red-500'>{error}</p>}
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex justify-end gap-3 pt-2 pb-4'>
                  <Button variant='outline' onClick={handleCancel} disabled={isSaving} size='md'>
                    {t('payment_additional_charges.cancel')}
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving || !additionalChargeType || !selectedMode} size='md'>
                    {isSaving
                      ? t('payment_additional_charges.saving')
                      : isEditMode
                      ? t('payment_additional_charges.update')
                      : t('payment_additional_charges.save')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentChargeForm
